import { useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { setTokenProvider } from '../services/apiClient';
import { apiConfig } from './authConfig';

/**
 * Hook that sets up authentication token provider for API client
 * This must be called once at the app level to enable authenticated API calls
 */
export function useAuthSetup() {
  const { instance, accounts, inProgress } = useMsal();

  useEffect(() => {
    const getAccessToken = async (): Promise<string> => {
      // Wait for MSAL to be initialized and not in progress
      if (inProgress !== 'none') {
        throw new Error('Authentication is in progress');
      }

      const account = accounts[0];
      
      if (!account) {
        throw new Error('No active account found');
      }

      const request = {
        scopes: apiConfig.scopes,
        account: account,
      };

      try {
        const response = await instance.acquireTokenSilent(request);
        return response.accessToken;
      } catch (error) {
        // If silent token acquisition fails, try redirect flow
        try {
          await instance.acquireTokenRedirect(request);
          // This will redirect the page, so we won't reach here
          throw new Error('Redirecting for authentication');
        } catch (redirectError) {
          console.error('Failed to acquire token via redirect:', redirectError);
          throw redirectError;
        }
      }
    };

    // Only set the token provider when MSAL is not in progress
    if (inProgress === 'none') {
      setTokenProvider(getAccessToken);
    }
  }, [instance, accounts, inProgress]);
}